import type { AssistantConfirmation } from '@/lib/api/assistant.api'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Check, ShieldCheck, X } from '@phosphor-icons/react'

import { describeArgs } from './confirm-fields'

/**
 * The write-confirmation card (Benny UX pass 3, R1). Rendered as a thread
 * message on both surfaces from `metadata.confirmation`: one line per write
 * the loop paused on, the fields it will set, and Approve / Skip. Nothing
 * runs until Approve; a newer message or 24 hours retires the card.
 * Personal accounts: adapted, same card.
 */
export function AssistantConfirmCard({
  confirmation,
  busy = false,
  onDecision,
  className,
}: {
  confirmation: AssistantConfirmation
  /** A decision is in flight (the resumed stream is running). */
  busy?: boolean
  /** Absent when the card is read-only (older turn, other surface). */
  onDecision?: (approve: boolean) => void
  className?: string
}) {
  const expired =
    confirmation.status === 'awaiting' &&
    new Date(confirmation.expiresAt).getTime() < Date.now()
  const status = expired ? 'expired' : confirmation.status
  const open = status === 'awaiting' && !!onDecision
  const items = confirmation.items ?? []
  const many = items.length > 1

  const caption =
    status === 'approved'
      ? 'Approved'
      : status === 'declined'
        ? 'Skipped'
        : status === 'superseded'
          ? 'Replaced by a newer message'
          : status === 'expired'
            ? 'Expired. Ask again.'
            : null

  return (
    <section
      className={cn(
        'rounded-card overflow-hidden border border-border/60 bg-background text-left shadow-sm',
        className,
      )}
      data-testid="assistant-confirm-card"
    >
      <div className="flex items-center gap-2 border-b border-border/40 px-4 py-2.5">
        <ShieldCheck
          className={cn(
            'h-4 w-4',
            status === 'approved'
              ? 'text-emerald-600'
              : status === 'awaiting'
                ? 'text-sky-600'
                : 'text-muted-foreground',
          )}
          weight="duotone"
        />
        <span className="text-xs font-medium text-muted-foreground">
          {status === 'awaiting'
            ? many
              ? `${items.length} changes need your approval`
              : 'Needs your approval'
            : caption}
        </span>
      </div>

      <ol className="divide-y divide-border/35 px-4">
        {items.map((item, index) => {
          const fields = describeArgs(item.args, item.resolvedNames)
          return (
            <li key={item.toolCallId} className="flex gap-3 py-3">
              {many && (
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sky-500/10 text-[10px] font-bold text-sky-700 dark:text-sky-300">
                  {index + 1}
                </span>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium leading-relaxed text-foreground">
                  {item.description}
                </p>
                {fields.length > 0 && (
                  <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
                    {fields.map(field => (
                      <div key={field.label + field.value} className="contents">
                        <dt className="text-muted-foreground">{field.label}</dt>
                        <dd className="min-w-0 truncate text-foreground/90">
                          {field.value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                )}
              </div>
            </li>
          )
        })}
      </ol>

      {open ? (
        <div className="flex items-center justify-between gap-2 border-t border-border/40 bg-muted/20 px-4 py-2.5">
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            Nothing runs until you approve.
          </p>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => onDecision?.(false)}
              className="h-8 rounded-full px-3 text-xs"
            >
              <X className="mr-1 h-3.5 w-3.5" /> Skip
            </Button>
            <Button
              size="sm"
              disabled={busy}
              onClick={() => onDecision?.(true)}
              className="h-8 rounded-full px-3.5 text-xs shadow-sm"
            >
              <Check className="mr-1 h-3.5 w-3.5" />
              {busy ? 'Running…' : many ? 'Approve all' : 'Approve'}
            </Button>
          </div>
        </div>
      ) : status === 'awaiting' ? (
        <p className="border-t border-border/40 px-4 py-2 text-[11px] text-muted-foreground">
          Waiting for your approval.
        </p>
      ) : null}
    </section>
  )
}
